import { handleRawDate } from "@/common/DateComponents";
import { stringToDate } from "@/common/validation/Validators";
import { DateInputFormat } from "@/components/common/DateComponents";
import { Actions as EMPLOYEEACTIONS } from "@/redux/actions/employeeActions";
import Tooltip from "@/tooltip";
import BuddySelector from "buddy/selector";
import cleanDeep from "clean-deep";
import { addMonths, addYears, format, parse, subYears } from "date-fns";
import { merge, setWith } from "lodash";
import { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { NotificationManager } from "react-notifications";
import { connect } from "react-redux";
import AgreementGrade from "../AgreementGrade";

const legalGenderOptions = [
	{ label: "Male", value: "Male" },
	{ label: "Female", value: "Female" },
];

const PersonalDetails = ({ nextPage, createNewEmployee, agreementInfo, jurisdiction, type, disabled }) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		// console.debug(field, value);
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);

		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};

	const {
		employeeDetails: employee,
		id: agreement,
		engagementDate,
		originalEngagement,
		enrolmentDeferredUntil,
	} = { ...agreementInfo };

	const agreementID = useMemo(() => {
		return String(agreement);
	}, [agreement]);

	useEffect(() => {
		const hasDetails = employee && Object.keys(cleanDeep(employee))?.length > 0;
		if (!hasDetails) return;
		setData({
			// * Personal Details
			title: employee?.title,
			givenName: employee?.givenName,
			familyName: employee?.familyName,
			legalGender: employee?.legalGender,
			dateOfBirth: employee?.dateOfBirth,
			email: employee?.email,
			engagementDate: engagementDate,
			originalEngagement: originalEngagement,
			enrolmentDeferredUntil: enrolmentDeferredUntil,
			// * Identification
			identity: (Array.isArray(employee?.identificationNumbers) ? employee?.identificationNumbers : [])?.reduce(
				(prev, curr) => {
					prev[curr.type] = curr.no;
					return prev;
				},
				{},
			),
			nationality: employee?.nationality,
		});
	}, [agreementID]);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!data.legalGender && ["UK"].includes(jurisdiction) && !["cis"].includes(type)) {
			NotificationManager.error("Legal Gender is required!", "Saving Employee");
			return;
		}

		let employeeDetails = {
			...data,
			engagementDate: undefined,
			originalEngagement: undefined,
			enrolmentDeferredUntil: undefined,
			identificationNumbers: [],
		};
		Object.entries({ ...data.identity }).map((index) => {
			const [type, no] = index;
			if (no != "") {
				employeeDetails.identificationNumbers.push({
					type,
					no,
					countryOfIssue: jurisdiction,
				});
			}
		});
		delete data.identity;

		createNewEmployee(
			cleanDeep(
				{
					type,
					engagementDate: data.engagementDate,
					originalEngagement: data.originalEngagement,
					enrolmentDeferredUntil: data.enrolmentDeferredUntil,
					employeeDetails,
				},
				{ emptyStrings: false, emptyArrays: false, emptyObjects: false },
			),
			nextPage,
		);
	};

	const defaultColSize = jurisdiction === "UK" ? "col-xs-3" : "col-xs-4";

	return (
		<form onSubmit={onSubmit}>
			<div className="col-xs-12 form-section">
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Employee Details</h1>
				</div>
				{jurisdiction == "UK" && (
					<div className={defaultColSize}>
						<div className="form-material form-container">
							<label htmlFor="title">Title</label>
							<input
								className="form-control form-input"
								type="text"
								id="title"
								name="title"
								value={data?.title}
								onChange={(e) => {
									updateField(e.target.name, e.target.value);
								}}
								autoComplete="off"
								disabled={disabled}
							/>
						</div>
					</div>
				)}

				<div className={defaultColSize}>
					<div className="form-material form-container">
						<label htmlFor="givenName">First Name</label>
						<input
							className="form-control form-input"
							id="givenName"
							name="givenName"
							value={data?.givenName}
							required="required"
							onChange={(e) => {
								updateField(e.target.name, e.target.value);
							}}
							autoComplete="off"
							disabled={disabled}
						/>
					</div>
				</div>

				<div className={defaultColSize}>
					<div className="form-material form-container">
						<label htmlFor="familyName">Last Name</label>
						<input
							className="form-control form-input"
							id="familyName"
							name="familyName"
							value={data?.familyName}
							required="required"
							onChange={(e) => {
								updateField(e.target.name, e.target.value);
							}}
							autoComplete="off"
							disabled={disabled}
						/>
					</div>
				</div>
				{["UK"].includes(jurisdiction) && !["cis"].includes(type) ? (
					<>
						<div className={defaultColSize}>
							<div className="form-material form-container">
								<label htmlFor="material-text">
									<Tooltip content={"Gender as legally stated on passport or identification"}>
										Legal Gender
									</Tooltip>
								</label>
								<BuddySelector
									name="legalGender"
									options={legalGenderOptions}
									value={legalGenderOptions?.find((item) => item.value === data?.legalGender)}
									onChange={(selected) => {
										updateField("legalGender", selected?.value || null);
									}}
								/>
							</div>
						</div>
					</>
				) : null}
				<div className={defaultColSize}>
					<div className="form-material form-container">
						<label htmlFor="email">
							<Tooltip content="The personal email address is used for employee login and sending payslips. Leave Blank if you don't have it">
								Personal Email Address
							</Tooltip>
						</label>
						<input
							id="email"
							className="form-control form-input"
							type="email"
							name="email"
							disabled={disabled}
							autoComplete="off"
							value={data?.email}
							onChange={(e) => {
								updateField(e.target.name, e.target.value);
							}}
							data-rule-email="true"
						/>
					</div>
				</div>
				{["MT"].includes(jurisdiction) ? (
					<>
						<div className={defaultColSize}>
							<div className="form-material form-container">
								<label htmlFor="material-text">I.D. Card #</label>
								<input
									className="form-control form-input"
									id="identity[NationalID]"
									name="identity[NationalID]"
									value={data?.identity?.["NationalID"]}
									required="required"
									autoComplete="off"
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
									disabled={disabled}
								/>
								{/* <div id="identity-card-exists" className="help-block animated fadeInDown hide">
                            We recognise this identity. This person will be asked to
                            share their details once you complete the agreement.
                        </div>
                        <div id="agreement-exists" className="help-block animated fadeInDown hide">
                            We recognise this identity. This person already has an
                            agreement with your company.
                            <a href="" target="_blank">
                                To edit this agreement click here.
                            </a>
                        </div> */}
							</div>
						</div>
					</>
				) : (
					<>
						<div className={defaultColSize}>
							<div className="form-material form-container">
								<input
									className="form-control form-input"
									id="payrollId"
									name="payrollId"
									value={data?.payrollId}
									onChange={(e) => {
										updateField(e.target.name, e.target.value);
									}}
									disabled={disabled}
									autoComplete="off"
								/>
								<label htmlFor="payrollId">Payroll ID</label>
							</div>
						</div>
						<div className="col-xs-3">
							<div className="form-material form-container">
								<input
									className="form-control form-input"
									id="identity[SocialSecurity]"
									name="identity[SocialSecurity]"
									value={data?.identity?.["SocialSecurity"]}
									onChange={(e) => {
										const value = e.target.value.toUpperCase();
										updateField(e.target.name, value);
									}}
									disabled={disabled}
									autoComplete="off"
									required={true}
								/>
								<label htmlFor="identity[SocialSecurity]">NI Number*</label>
							</div>
						</div>
					</>
				)}
				<div className={defaultColSize}>
					<div className="form-material form-container">
						<DatePicker
							autoComplete="off"
							dateFormat="dd MMM yyyy"
							minDate={subYears(new Date(), 100)}
							maxDate={subYears(new Date(), 14)}
							wrapperClassName="full-width"
							required={true}
							customInput={<DateInputFormat />}
							className="form-control form-input"
							onChange={(date) => {
								updateField("dateOfBirth", date ? format(date, "yyyy-MM-dd") : undefined);
							}}
							onChangeRaw={(e) => {
								handleRawDate(e.target.value, (date) => {
									updateField("dateOfBirth", date ? format(date, "yyyy-MM-dd") : undefined);
								});
							}}
							strictParsing
							selected={data.dateOfBirth && parse(data.dateOfBirth, "yyyy-MM-dd", new Date())}
						/>
						<label htmlFor="dateOfBirth">Date of Birth</label>
					</div>
				</div>
				<div className="clearfix" />

				<AgreementGrade
					forNewEmployee={true}
					isFirstPage={true}
				/>
			</div>
			<div className="clearfix" />

			<div className="col-xs-12 form-section">
				<div className="col-xs-12 section-header">
					<h1 className="block-title">Agreement Dates</h1>
				</div>
				<div className="col-xs-4">
					<div className="form-material form-container">
						<label htmlFor="material-text">
							<Tooltip content="Enter the date this employee was hired on with this company.">
								Date of Employment*
							</Tooltip>
						</label>

						<DatePicker
							autoComplete="off"
							dateFormat="dd MMM yyyy"
							minDate={subYears(new Date(), 100)}
							maxDate={addYears(new Date(), 100)}
							required={true}
							customInput={<DateInputFormat />}
							wrapperClassName="full-width"
							className="form-control form-input"
							onChange={(date) => {
								updateField("engagementDate", date ? format(date, "yyyy-MM-dd") : undefined);
							}}
							onChangeRaw={(e) => {
								handleRawDate(e.target.value, (date) => {
									updateField("engagementDate", date ? format(date, "yyyy-MM-dd") : undefined);
								});
							}}
							strictParsing
							selected={data.engagementDate && parse(data.engagementDate, "yyyy-MM-dd", new Date())}
						/>
					</div>
				</div>

				<div className="col-xs-4">
					<div className="form-material form-container">
						<label htmlFor="material-text">
							<Tooltip content="If this employee was moved from another company, add their original employed to rebase calculations such as leave entitlements & minimum wage on this date instead.">
								Original Date Employed
							</Tooltip>
						</label>

						<DatePicker
							autoComplete="off"
							dateFormat="dd MMM yyyy"
							minDate={subYears(new Date(), 100)}
							maxDate={addYears(new Date(), 100)}
							customInput={<DateInputFormat />}
							wrapperClassName="full-width"
							className="form-control form-input"
							onChange={(date) => {
								updateField("originalEngagement", date ? format(date, "yyyy-MM-dd") : undefined);
							}}
							onChangeRaw={(e) => {
								handleRawDate(e.target.value, (date) => {
									updateField("originalEngagement", date ? format(date, "yyyy-MM-dd") : undefined);
								});
							}}
							strictParsing
							selected={
								data.originalEngagement && parse(data.originalEngagement, "yyyy-MM-dd", new Date())
							}
						/>
					</div>
				</div>

				{jurisdiction == "UK"  && !["cis"].includes(type) && (
					<div className="col-xs-4">
						<div className="form-material form-container">
							<DatePicker
								autoComplete="off"
								dateFormat="dd MMM yyyy"
								disabled={!data?.engagementDate}
								minDate={data?.engagementDate ? stringToDate(data?.engagementDate) : null}
								maxDate={data?.engagementDate ? addMonths(stringToDate(data?.engagementDate), 3) : null}
								customInput={<DateInputFormat />}
								wrapperClassName="full-width"
								className="form-control form-input"
								onChange={(date) => {
									updateField("enrolmentDeferredUntil", date ? format(date, "yyyy-MM-dd") : null);
								}}
								onChangeRaw={(e) => {
									handleRawDate(e.target.value, (date) => {
										updateField("enrolmentDeferredUntil", date ? format(date, "yyyy-MM-dd") : null);
									});
								}}
								strictParsing
								isClearable
								selected={
									data.enrolmentDeferredUntil ? stringToDate(data.enrolmentDeferredUntil) : null
								}
							/>
							<label htmlFor="enrolmentDeferredUntil">
								<Tooltip
									content={
										"You can choose to delay working out which pension scheme they are enrolled in for up to three months after their engagement date"
									}
								>
									Postpone Auto Enrolment
								</Tooltip>
							</label>
						</div>
					</div>
				)}
			</div>
			<div className="clearfix" />

			<div>
				<button
					className="btn btn-success right"
					type="submit"
				>
					Save &amp; Continue
				</button>
			</div>
			<div className="clearfix" />
		</form>
	);
};

const mapStateToProps = (state) => {
	return {
		jurisdiction: state.company?.settings?.jurisdiction,
		agreementInfo: state?.employee?.agreementInfo,
	};
};

const mapDispatchToProps = (dispatch) => ({
	createNewEmployee: (body, onSuccess) => dispatch(EMPLOYEEACTIONS.createNewEmployee(body, onSuccess)),
	updateEmployee: (body) => dispatch(EMPLOYEEACTIONS.updateEmployee(body)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PersonalDetails);
